import React, { useMemo } from 'react';
import ClassroomDetail from './ClassroomDetail';
import './ClassroomDetails.css'; // Using dedicated CSS file instead of ClassroomRatios.css
import SmartcareSignInButton from './SmartcareSignInButton';
import ClassroomTransferButton from './ClassroomTransferButton';
import TooltipComponent from './TooltipComponent';
import { processedClassroomData, useClassroomData } from '../data/classroomData';

interface ClassroomDetailsProps {
  refreshTrigger?: number; // Optional prop to trigger refresh
}

const ClassroomDetails: React.FC<ClassroomDetailsProps> = ({ refreshTrigger }) => {
  const classroomData = useClassroomData().data;
  const { sections, specialCategories } = useMemo(()=> processedClassroomData(classroomData), [classroomData]);


  return (
    <div className="classroom-details-component classrooms-detail">
      <h2>Classroom Details</h2>
      {sections.map(section => (
        <div key={section.name} className="section-container">
          <div className={`ratio-${section.ratioStatus}`}>
            <span className="section-name">{section.name}</span>
            <span className="section-ratio">
              {section.displayRatio} ({section.totalStudents}/{section.totalActiveTeachers})
            </span>
          </div>
          <div className="classroom-grid">
            {section.classrooms.map(classroom => (
              <ClassroomDetail key={classroom.id} classroom={classroom} />
            ))}
          </div>
        </div>
      ))}
      
      {/* Special staff sections - office and unassigned */}
      <div className="special-staff-sections">
        {specialCategories.map((classroom) => {
          // Check if any teacher is missing SmartCare ID
          const hasTeachersMissingId = classroom.teachers.some(teacher => !teacher.sc_id);
          // Check if any teacher needs to sign into SmartCare
          const hasTeachersNotSignedIn = classroom.teachers.some(teacher => teacher.sc_id && !teacher.sc_checkedin);
          // Check if any teacher is not clocked into WhenIWork
          const hasTeachersNotClockedIn = classroom.teachers.some(teacher => teacher.wiw_id && !teacher.wiw_clockedin);
          
          return (
            <div key={classroom.id} className="special-staff-section">
              <h3>{classroom.name} Staff</h3>
              {classroom.teachers.length > 0 ? (
                <>
                  <ul className="staff-list">
                    {classroom.teachers.map(teacher => (
                      <li key={teacher.sc_id || teacher.wiw_id} className={teacher.onBreak ? 'on-break' : ''}>
                        <span className="name">
                          {teacher.name}
                          {!teacher.sc_id && 
                            <span className="missing-id-icon" title="Does not exist in Smartcare"></span>
                          }
                          {teacher.wiw_shift && (
                            <TooltipComponent 
                              text={`🕛`}
                              tooltipText={`${new Date(teacher.wiw_shift.start_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${new Date(teacher.wiw_shift.end_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`}                    
                            />

                          )}
                          {teacher.sc_id && !teacher.sc_checkedin && 
                            <SmartcareSignInButton teacher={teacher} currentClassroomId={classroom.id} />
                          }
                          {teacher.wiw_id && !teacher.wiw_clockedin && 
                            <span className="not-clocked-icon" title="Teacher is not clocked in to WhenIWork"></span>
                          }
                          <ClassroomTransferButton 
                            teacher={teacher} 
                            currentClassroomId={classroom.id} 
                          />
                        </span>
                        {teacher.onBreak && <span className="break-tag">ON BREAK</span>}
                      </li>
                    ))}
                  </ul>
                  
                  {(hasTeachersNotSignedIn || hasTeachersMissingId || hasTeachersNotClockedIn) && (
                    <div className="tooltip-legend">
                      {hasTeachersNotSignedIn && (
                        <>
                          <span className="signin-icon-button"></span>
                          <span className="legend-text">Click to sign user into Smartcare</span>
                        </>
                      )}
                      {hasTeachersNotSignedIn && (hasTeachersMissingId || hasTeachersNotClockedIn) && <br />}
                      {hasTeachersMissingId && (
                        <>
                          <span className="missing-id-icon"></span>
                          <span className="legend-text">Does not exist in Smartcare</span>
                        </>
                      )}
                      {hasTeachersMissingId && hasTeachersNotClockedIn && <br />}
                      {hasTeachersNotClockedIn && (
                        <>
                          <span className="not-clocked-icon"></span>
                          <span className="legend-text">Teacher is not clocked in to WhenIWork</span>
                        </>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p className="empty-message">No staff in this category</p>
              )}
            </div>
          );
        })}
      </div>      
    </div>
  );
};

export default ClassroomDetails;
